import useSWR from 'swr';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Global fetcher with auth
const fetcher = async (url) => {
  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const response = await axios.get(url, { headers });
  return response.data;
};

// Fetcher for public endpoints (no auth needed)
const publicFetcher = async (url) => {
  const response = await axios.get(url);
  return response.data;
};

// SWR configuration for fast, cached data
const swrConfig = {
  revalidateOnFocus: false,      // Don't refetch when window regains focus
  revalidateOnReconnect: true,   // Refetch when network reconnects
  dedupingInterval: 60000,       // Dedupe requests within 60 seconds
  errorRetryCount: 2,            // Retry failed requests twice
  keepPreviousData: true,        // Show stale data while revalidating
};

// ============ DASHBOARD HOOKS ============

export function useDashboardData() {
  const { data, error, isLoading, mutate } = useSWR(
    `${API}/dashboard/stats`,
    fetcher,
    { ...swrConfig, refreshInterval: 120000 } // Refresh every 2 min
  );
  
  return {
    stats: data,
    isLoading,
    isError: error,
    refresh: mutate
  };
}

export function useWallets() {
  const { data, error, isLoading, mutate } = useSWR(
    `${API}/wallets`,
    fetcher,
    { ...swrConfig, refreshInterval: 120000 }
  );
  
  return {
    wallets: data || [],
    isLoading,
    isError: error,
    refresh: mutate
  };
}

export function useBankAccount(email) {
  const { data, error, isLoading } = useSWR(
    email ? `${API}/banking/onboarding/accounts?email=${email}` : null,
    fetcher,
    swrConfig
  );
  
  return {
    bankAccount: data?.accounts?.[0] || null,
    isLoading,
    isError: error
  };
}

export function useCards(email) {
  const { data, error, isLoading } = useSWR(
    email ? `${API}/cards/my-cards?email=${email}` : null,
    fetcher,
    swrConfig
  );
  
  return {
    cards: data?.cards || [],
    isLoading,
    isError: error
  };
}

// ============ VIP & KYC HOOKS ============

export function useVipStatus() {
  const { data, error, isLoading } = useSWR(
    `${API}/vip/status`,
    fetcher,
    { ...swrConfig, refreshInterval: 300000 } // Refresh every 5 min
  );
  
  return {
    vipStatus: data,
    isLoading,
    isError: error
  };
}

export function useKycStatus() {
  const { data, error, isLoading, mutate } = useSWR(
    `${API}/kyc/sumsub/status`,
    fetcher,
    swrConfig
  );
  
  return {
    kycStatus: data,
    isLoading,
    isError: error,
    refresh: mutate
  };
}

export function useBusinessStatus() {
  const { data, error, isLoading } = useSWR(
    `${API}/business/status`,
    fetcher,
    swrConfig
  );
  
  return {
    businessStatus: data,
    isLoading,
    isError: error
  };
}

// ============ PRICE & MARKET HOOKS ============

export function useLivePrices() {
  const { data, error, isLoading, mutate } = useSWR(
    `${API}/prices/live`,
    publicFetcher,
    { ...swrConfig, refreshInterval: 60000 } // Refresh every minute
  );
  
  return {
    prices: data?.prices || [],
    isLoading,
    isError: error,
    refresh: mutate
  };
}

export function useTokenPrices() {
  const { data, error, isLoading } = useSWR(
    `${API}/prices/latest`,
    publicFetcher,
    { ...swrConfig, refreshInterval: 60000 }
  );
  
  return {
    prices: data?.prices || {},
    isLoading,
    isError: error
  };
}

export function useNetworkStatus() {
  const { data, error, isLoading } = useSWR(
    `${API}/blockchain/networks/status`,
    publicFetcher,
    { ...swrConfig, refreshInterval: 300000 }
  );
  
  return {
    status: data?.status || {},
    isLoading,
    isError: error
  };
}

// ============ STAKING HOOKS ============

export function useStakingPools() {
  const { data, error, isLoading } = useSWR(
    `${API}/staking/pools`,
    publicFetcher,
    { ...swrConfig, refreshInterval: 300000 }
  );
  
  return {
    pools: data?.pools || [],
    isLoading,
    isError: error
  };
}

export function useUserStakes() {
  const { data, error, isLoading, mutate } = useSWR(
    `${API}/staking/user-stakes`,
    fetcher,
    swrConfig
  );
  
  return {
    stakes: data?.stakes || [],
    isLoading,
    isError: error,
    refresh: mutate
  };
}

// ============ NOTIFICATIONS ============

export function useNotifications() {
  const { data, error, isLoading, mutate } = useSWR(
    `${API}/notifications`,
    fetcher,
    { ...swrConfig, refreshInterval: 30000 } // Check every 30 sec
  );
  
  return {
    notifications: data || [],
    isLoading,
    isError: error,
    refresh: mutate
  };
}

// ============ SECURITY ============

export function useTwoFactorStatus() {
  const { data, error, isLoading, mutate } = useSWR(
    `${API}/auth/2fa/status`,
    fetcher,
    swrConfig
  );
  
  return {
    twoFactorStatus: data,
    isLoading,
    isError: error,
    refresh: mutate
  };
}

export function useSecurityOverview() {
  const { data, error, isLoading } = useSWR(
    `${API}/security/overview`,
    fetcher,
    swrConfig
  );
  
  return {
    security: data,
    isLoading,
    isError: error
  };
}

// ============ UTILITY ============

// Prefetch data for common navigation paths
export function prefetchDashboardData() {
  const token = localStorage.getItem('token');
  if (!token) return;
  
  const headers = { Authorization: `Bearer ${token}` };
  
  // Prefetch in background
  Promise.all([
    axios.get(`${API}/wallets`, { headers }).catch(() => {}),
    axios.get(`${API}/vip/status`, { headers }).catch(() => {}),
    axios.get(`${API}/kyc/sumsub/status`, { headers }).catch(() => {}),
    axios.get(`${API}/prices/live`).catch(() => {}),
  ]);
}

// Clear all cached data (useful on logout)
export function clearCache() {
  // SWR's cache is automatically managed, but we can trigger revalidation
  window.location.reload();
}

export { fetcher, publicFetcher, swrConfig };
